import React, { useMemo, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCode } = FiIcons;

const RichTextEditor = ({ value, onChange, placeholder = '', className = '' }) => {
  const [showHtml, setShowHtml] = useState(false);
  const [htmlSource, setHtmlSource] = useState(value || '');

  // Update htmlSource when value changes externally
  useEffect(() => {
    if (!showHtml) {
      setHtmlSource(value || '');
    }
  }, [value, showHtml]);

  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['code-block'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link',
    'code-block'
  ];

  const handleHtmlToggle = () => {
    if (showHtml) {
      // Switching from HTML to visual - update the editor with HTML content
      onChange(htmlSource);
    } else {
      // Switching to HTML view - get current HTML from editor
      setHtmlSource(value || '');
    }
    setShowHtml(!showHtml);
  };

  const handleHtmlChange = (e) => {
    setHtmlSource(e.target.value);
  };

  const handleHtmlSave = () => {
    onChange(htmlSource);
    setShowHtml(false);
  };

  return (
    <div className={`rich-text-editor ${className}`}>
      {showHtml ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">HTML Source</span>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleHtmlSave}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Save HTML
              </button>
              <button
                type="button"
                onClick={handleHtmlToggle}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
          <textarea
            value={htmlSource}
            onChange={handleHtmlChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            rows={10}
            placeholder="Enter HTML code here..."
          />
        </div>
      ) : (
        <div>
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={handleHtmlToggle}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              title="Edit HTML source"
            >
              <SafeIcon icon={FiCode} className="text-sm" />
              <span>HTML</span>
            </button>
          </div>
          <ReactQuill
            theme="snow"
            value={value || ''}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
            }}
          />
        </div>
      )}
      <style>{`
        .rich-text-editor .ql-container {
          min-height: 150px;
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
        .rich-text-editor .ql-editor {
          min-height: 150px;
        }
        .rich-text-editor .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          border: 1px solid #d1d5db;
          border-bottom: none;
        }
        .rich-text-editor .ql-container {
          border: 1px solid #d1d5db;
          border-top: none;
        }
        .rich-text-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;

